import queryString from 'query-string'

import type {
  IIntegralConclusionDto,
  IMachineInfoDto,
  IMachineIntegralSummary,
  TCreateMachine,
  TUpdateMachine,
  TUpdateWorkModes
} from '@web/entities/machine'
import type { IExpertAssessmentCreate } from '@web/entities/machine/api/assessment.types'
import type { EMeasurementType } from '@web/entities/measurement/model/enums/measurement-type.enum'
import type { ILogicalExpression } from '@web/entities/work-mode/model/logical-expression.type'
import { ApiResource, ApiTag } from '@web/shared/constants/api.constants'
import { baseApi } from '@web/shared/infrastructure/api-base-query'
import type { ICopyDto } from '@web/shared/types/copy-dto.type'
import type { TId } from '@web/shared/types/id.type'
import type { IPagination } from '@web/shared/types/paged-response.type'
import type { TQueryParams } from '@web/shared/types/query-params.type'

export const machinesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMachines: builder.query<
      IPagination<IMachineInfoDto>,
      TQueryParams<{ groupId: TId; onlyRoot: boolean }>
    >({
      query: (params) => {
        const query = queryString.stringify({ ...params })
        return { url: `${ApiResource.Machines}?${query}` }
      },
      providesTags: [ApiTag.Machines]
    }),

    createMachine: builder.mutation<{ id: string }, TCreateMachine>({
      query: (data) => ({
        url: ApiResource.Machines,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [ApiTag.Machines]
    }),

    getMachine: builder.query<IMachineInfoDto, { machineId: TId }>({
      query: ({ machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}`
      }),
      providesTags: [ApiTag.Machine]
    }),

    getMachineTechnicalCondition: builder.query<
      IIntegralConclusionDto,
      { machineId: TId }
    >({
      query: ({ machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}/technical-condition`
      }),
      providesTags: [ApiTag.Machine]
    }),

    updateMachine: builder.mutation<
      null,
      { body: TUpdateMachine; machineId: string }
    >({
      query: ({ body, machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.Machines, ApiTag.Machine]
    }),

    updateMachinesStatus: builder.mutation<
      null,
      { updateData: { paused: boolean }; machineId: string }
    >({
      query: ({ updateData, machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}/paused`,
        method: 'PUT',
        body: updateData
      }),
      invalidatesTags: [ApiTag.GetAllNodes, ApiTag.Machine]
    }),

    updateMachinesWorkModeControlled: builder.mutation<
      null,
      { workModeControlled: boolean; machineId: string }
    >({
      query: ({ workModeControlled, machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}/work-mode-controlled`,
        method: 'PUT',
        body: { workModeControlled }
      }),
      invalidatesTags: [ApiTag.GetAllNodes, ApiTag.Machine]
    }),

    updateLogicExpressionMachine: builder.mutation<
      null,
      { machineId: TId; body: ILogicalExpression }
    >({
      query: ({ machineId, body }) => ({
        url: `${ApiResource.Machines}/${machineId}/logic-expression`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.Machine]
    }),

    updateMachineReorder: builder.mutation<
      void,
      { machineId: string; data: { order: number; parentId: string | null } }
    >({
      query: ({ machineId, data }) => ({
        url: `${ApiResource.Machines}/${machineId}/reorder`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.GetAllNodes, ApiTag.Machines, ApiTag.Machine]
    }),

    deleteMachine: builder.mutation<null, TId>({
      query: (id) => ({
        url: `${ApiResource.Machines}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.Machines]
    }),

    deleteLogicExpressionMachine: builder.mutation<
      null,
      {
        machineId: TId
        level: number
      }
    >({
      query: ({ machineId, level }) => ({
        url: `${ApiResource.Machines}/${machineId}/logic-expression/${level}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.Machine]
    }),

    updateWorkModeMachine: builder.mutation<
      null,
      { body: TUpdateWorkModes; machineId: TId }
    >({
      query: ({ body, machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}/work-modes`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.Machines, ApiTag.Machine]
    }),

    updateViewingDepthSecondsMachine: builder.mutation<
      null,
      { body: { viewingDepthSeconds: number }; machineId: TId }
    >({
      query: ({ body, machineId }) => ({
        url: `${ApiResource.Machines}/${machineId}/viewing-depth-seconds`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.Machines]
    }),

    getMachineIntegralSummary: builder.query<
      IMachineIntegralSummary,
      { machineId: TId; priorityTypes?: EMeasurementType[] }
    >({
      query: ({ machineId, priorityTypes }) => {
        const params = queryString.stringify(
          { priorityTypes },
          { arrayFormat: 'comma' }
        )

        return {
          url: `${ApiResource.Machines}/${machineId}/integral-scheme-summary?${params}`,
          method: 'GET'
        }
      },
      providesTags: [ApiTag.Machines]
    }),

    getMachineIntegralConclusion: builder.query<
      IIntegralConclusionDto,
      { machineId: TId }
    >({
      query: ({ machineId }) => {
        const params = queryString.stringify({ machineId })

        return {
          url: `${ApiResource.Machines}/${machineId}/integral-scheme-conclusion?${params}`,
          method: 'GET'
        }
      },
      providesTags: [ApiTag.Machines]
    }),

    createExpertAssessment: builder.mutation<
      void,
      { body: IExpertAssessmentCreate; machineId: TId; assessmentId: TId }
    >({
      query: ({ body, machineId, assessmentId }) => ({
        url: `${ApiResource.Machines}/${machineId}/expert-assessment/${assessmentId}`,
        body,
        method: 'POST'
      }),
      invalidatesTags: [ApiTag.Machines]
    }),

    copyMachine: builder.mutation<void, ICopyDto>({
      query: (body) => ({
        url: `${ApiResource.Machines}/copy`,
        method: 'POST',
        body
      }),
      invalidatesTags: [
        ApiTag.Machines,
        ApiTag.MeasuringPoints,
        ApiTag.MeasuringPoint,
        ApiTag.Measurements,
        ApiTag.MeasurementsTree
      ]
    })
  })
})

export const {
  useGetMachinesQuery,
  useLazyGetMachinesQuery,
  useCreateMachineMutation,
  useGetMachineQuery,
  useGetMachineTechnicalConditionQuery,
  useLazyGetMachineQuery,
  useUpdateMachineMutation,
  useUpdateLogicExpressionMachineMutation,
  useDeleteLogicExpressionMachineMutation,
  useUpdateMachineReorderMutation,
  useUpdateWorkModeMachineMutation,
  useUpdateViewingDepthSecondsMachineMutation,
  useGetMachineIntegralConclusionQuery,
  useGetMachineIntegralSummaryQuery,
  useCreateExpertAssessmentMutation,
  useDeleteMachineMutation,
  useUpdateMachinesStatusMutation,
  useUpdateMachinesWorkModeControlledMutation,
  useCopyMachineMutation
} = machinesApi
